/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import { UserFull } from '@/api/types/user';
import { SecretSearch, SecretPost, SecretComment, SecretCommentPost, SecretEmotion } from '@/api/types/secret';
import { SecretBlocksItem } from '.';
import { SecretContentItem } from '../secret-content';

export interface SecretContentsParserOptions {
  emotions?: SecretEmotion[];
  parsers?: {
    topic?: boolean;
    emotion?: boolean;
    serial?: boolean;
    post?: boolean;
    name?: boolean;
    newline?: boolean;
    url?: boolean;
    music?: boolean;
    markdownLink?: boolean;
  };
}

const getCharType = (code: number): 'number' | 'alphabet' | 'zh-Hans' | 'unknown' => {
  if (code >= 48 && code <= 57) {
    return 'number';
  }
  if ((code >= 97 && code <= 122) || (code >= 65 && code <= 90)) {
    return 'alphabet';
  }
  if ((code >= 0x3400 && code <= 0xa4ff) || (code >= 0xf900 && code <= 0xfaff)) {
    return 'zh-Hans';
  }
  return 'unknown';
};

/** 正文解码状态机状态 */
type ParseContentsAction = 'text' | 'newline' | 'topic' | 'serial'
| 'emotion' | 'name' | 'post'
| 'url' | 'music' | 'music-src' | 'markdownlink' | 'markdownlink-url';

/** 正文解码状态机 */
interface ParseContentsFSM {
  readonly options: {
    readonly parsers: Partial<Record<ParseContentsAction, boolean>>;
    readonly emotions: Record<string, SecretEmotion>;
    readonly emotionMaxLen: number;
  };
  state: {
    /** 解析的字符列表 */
    readonly chars: string[];
    /** 解析结果 */
    contents: SecretContentItem[];
    /** 当前状态机状态 */
    action: ParseContentsAction;
    /** 首个未解析的下标地址 */
    pending: number;
    /** 本次模式匹配猜测起始地址，用于回溯和处理前段文本 */
    start: number;
    /** 迭代器位置 */
    pos: number;
    /** 段落换行符位置 */
    newlinePos: number;
    /** 特殊匹配串位置记录点，注：需自行处理状态机变化导致脏数据的问题 */
    extraPos: number[];
    /** 当前解析的字符 */
    char: string;
    /** 当前解析的字符二进制值 */
    code: number;
    /** 当前解析的字符类型 */
    type: ReturnType<typeof getCharType>;
  };
  /** 当前状态机转移函数 */
  next?: (options: ParseContentsFSM['options'], state: ParseContentsFSM['state']) => void;
}

/**
 * 识别到模式匹配后，将前序未处理的文本加入结果数组中
 * @param {ParseContentsFSM.state} state 当前状态机
 * @returns {void}
 */
const appendPendingText = (state: ParseContentsFSM['state']): void => {
  if (state.pending === state.start) {
    return;
  }
  const text = state.chars.slice(state.pending, state.start).join('');
  const last = state.contents.length && state.contents[state.contents.length - 1];
  if (last && last.type === 'text') {
    last.text += text;
  } else {
    state.contents.push({ type: 'text', text });
  }
};

/**
 * 解析规则优先级策略表
 * 逻辑： 解析失败回溯解析位置到起始点，并按优先级次序依次更改状态机状态。
 */
const parserDowngradeMap: Record<string, ParseContentsAction[]> = {
  // “#”解析规则 话题 > 楼层引用 > 表情
  '#': ['topic', 'serial', 'emotion'],
  '@': ['name', 'post'],
};

/**
 * 根据当前解析字符和解析状态以及解析器参数，推导出下一个可能满足的解析类型
 * @param {SecretContentsParserOptions} options 解析器参数
 * @param {string} char 当前解析匹配字符
 * @param {ParseContentsAction} parser 当前解析状态
 * @returns {ParseContentsAction} 下一个解析类型
 */
const nextParser = (options: ParseContentsFSM['options'], char: string, parser?: ParseContentsAction): ParseContentsAction => {
  const list = parserDowngradeMap[char];
  if (!list) {
    return 'text';
  }
  let find = !parser;
  for (let i = 0; i < list.length; i += 1) {
    const p = list[i];
    if (!find && list[i - 1] === parser) {
      find = true;
    }
    if (find && options.parsers[p]) {
      return p;
    }
  }
  return 'text';
};

/** 正文解码状态转移函数 */
const ParseContentsActionHandle: Partial<Record<ParseContentsAction, ParseContentsFSM['next']>> = {
  text: (options, state) => {
    if (state.char === '#') {
      state.start = state.pos;
      state.action = nextParser(options, '#');
    } else if (state.char === '@') {
      state.start = state.pos;
      state.action = nextParser(options, '@');
    } else if (options.parsers.url && (state.char === 'h' || state.char === 'H')
      && (state.chars[state.pos + 1] === 't' || state.chars[state.pos + 1] === 'T')
      && (state.chars[state.pos + 2] === 't' || state.chars[state.pos + 2] === 'T')
      && (state.chars[state.pos + 3] === 'p' || state.chars[state.pos + 3] === 'P')
      && (state.chars[state.pos + 4] === 's' || state.chars[state.pos + 4] === 'S')
      && state.chars[state.pos + 5] === ':'
      && state.chars[state.pos + 6] === '/'
      && state.chars[state.pos + 7] === '/'
    ) {
      state.start = state.pos;
      state.pos += 7;
      state.action = 'url';
    } else if (options.parsers.url && (state.char === 'h' || state.char === 'H')
      && (state.chars[state.pos + 1] === 't' || state.chars[state.pos + 1] === 'T')
      && (state.chars[state.pos + 2] === 't' || state.chars[state.pos + 2] === 'T')
      && (state.chars[state.pos + 3] === 'p' || state.chars[state.pos + 3] === 'P')
      && state.chars[state.pos + 4] === ':'
      && state.chars[state.pos + 5] === '/'
      && state.chars[state.pos + 6] === '/'
    ) {
      state.start = state.pos;
      state.pos += 6;
      state.action = 'url';
    } else if (options.parsers.newline && state.char === '\n') {
      state.start = state.pos;
      appendPendingText(state);
      state.contents.push({
        type: 'newline',
        paragraphLen: state.pos - state.newlinePos,
      });
      state.pending = state.pos + 1;
      state.start = state.pending;
      state.newlinePos = state.pos;
    } else if (options.parsers.music && state.char === '{' && state.chars[state.pos + 1] === '~') {
      state.start = state.pos;
      state.pos += 1;
      state.action = 'music';
    } else if (options.parsers.markdownlink && state.char === '[') {
      state.start = state.pos;
      state.action = 'markdownlink';
    }
    // 解析结束，没有新的模式匹配
    if (!state.char) {
      state.start = state.pos;
      appendPendingText(state);
      state.pending = state.pos;
    }
  },
  topic: (options, state) => {
    if (state.pos === state.start + 1) {
      state.extraPos[0] = 0;
      state.extraPos[1] = 0;
      state.extraPos[2] = 0;
    }
    if (state.char === '#' && state.pos > state.start + 2 && state.pos < state.start + 32 && state.extraPos[0]) {
      // 匹配成功结束
      appendPendingText(state);
      state.contents.push({
        type: 'topic',
        topic: state.chars.slice(state.start + 1, state.pos).join(''),
      });
      state.pending = state.pos + 1;
      state.start = state.pending;
      state.action = 'text';
    } else if (!state.char || state.char === '#' || (state.type !== 'alphabet' && state.type !== 'number' && state.type !== 'zh-Hans' && state.char !== ' ')) {
      // 匹配失败，按“#”解析规则优先级进入下个状态
      state.pos = state.start;
      state.action = nextParser(options, '#', 'topic');
    } else if (state.type === 'number') {
      // 标记纯数字内容
      state.extraPos[1] = state.pos;
      if (state.extraPos[2]) {
        state.extraPos[0] = state.pos;
      }
    } else if (state.char === ' ') {
      // 标记非纯数字内容：空格不可在收尾
      state.extraPos[2] = state.pos;
    } else {
      // 标记非纯数字内容
      state.extraPos[0] = state.pos;
    }
  },
  serial: (options, state) => {
    if (!state.char || state.type !== 'number') {
      if (state.pos <= state.start + 1) {
        // 匹配失败，按“#”解析规则优先级进入下个状态
        state.pos = state.start;
        state.action = nextParser(options, '#', 'serial');
      } else {
        // 匹配成功结束
        appendPendingText(state);
        state.contents.push({
          type: 'serial',
          serial: parseInt(state.chars.slice(state.start + 1, state.pos).join(''), 10),
        });
        state.pending = state.pos;
        state.pos -= 1;
        state.action = 'text';
      }
    }
  },
  emotion: (options, state) => {
    for (let i = options.emotionMaxLen; i > 1; i -= 1) {
      const emotion = options.emotions[state.chars.slice(state.start + 1, state.start + 1 + i).join('')];
      if (emotion) {
        // 匹配成功结束
        appendPendingText(state);
        state.contents.push({
          type: 'emotion',
          emotion,
        });
        state.pending = state.pos + i;
        state.pos += i - 1;
        state.action = 'text';
        break;
      }
    }
    if (state.action === 'emotion') {
      // 匹配失败，按“#”解析规则优先级进入下个状态
      state.pos = state.start;
      state.action = nextParser(options, '#', 'emotion');
    }
  },
  name: (options, state) => {
    if (state.pos > state.start + 1 && state.type !== 'alphabet' && state.type !== 'number' && state.type !== 'zh-Hans' && state.char !== '_') {
      // 匹配完成
      appendPendingText(state);
      state.contents.push({
        type: 'name',
        name: state.chars.slice(state.start + 1, state.pos).join(''),
      });
      state.pending = state.pos;
      state.pos -= 1;
      state.action = 'text';
    } else if (state.pos === state.start + 1 && state.type !== 'zh-Hans' && state.type !== 'alphabet') {
      // 匹配失败，按“@”解析规则优先级进入下个状态
      state.pos = state.start;
      state.action = nextParser(options, '@', 'name');
    }
  },
  url: (options, state) => {
    if (state.type !== 'alphabet' && state.type !== 'number' && state.char !== '.' && state.char !== '/' && state.char !== '#' && state.char !== '?' && state.char !== '%' && state.char !== '-' && state.char !== '=' && state.char !== '&' && state.char !== '!') {
      appendPendingText(state);
      state.contents.push({
        type: 'url',
        url: state.chars.slice(state.start, state.pos).join(''),
      });
      state.pending = state.pos;
      state.pos -= 1;
      state.action = 'text';
    }
  },
  post: (options, state) => {
    if (state.type !== 'number') {
      if (state.pos > state.start + 1) {
        // 匹配成功
        appendPendingText(state);
        state.contents.push({
          type: 'post',
          id: state.chars.slice(state.start + 1, state.pos).join(''),
        });
        state.pending = state.pos;
        state.pos -= 1;
        state.action = 'text';
      } else {
        // 匹配失败，按“@”解析规则优先级进入下个状态
        state.pos = state.start;
        state.action = nextParser(options, '@', 'post');
      }
    }
  },
  music: (options, state) => {
    if (!state.char || state.char === '\n') {
      // 匹配失败，回溯状态机
      state.pos = state.start;
      state.action = 'text';
    } else if (state.char === '|') {
      state.extraPos[0] = state.pos;
      state.action = 'music-src';
    }
  },
  'music-src': (options, state) => {
    if (!state.char || state.char === '\n') {
      // 匹配失败，回溯状态机
      state.pos = state.start;
      state.action = 'text';
    } else if (state.char === '}' && state.chars[state.pos - 1] === '~') {
      appendPendingText(state);
      state.contents.push({
        type: 'music',
        label: state.chars.slice(state.start + 2, state.extraPos[0]).join(''),
        src: state.chars.slice(state.extraPos[0] + 1, state.pos - 1).join(''),
      });
      state.pending = state.pos + 1;
      state.action = 'text';
      state.newlinePos = state.pending;
    }
  },
  markdownlink: (options, state) => {
    if (!state.char || state.char === '\n') {
      // 匹配失败，回溯状态机
      state.pos = state.start;
      state.action = 'text';
    } else if (state.char === '(' && state.chars[state.pos - 1] === ']') {
      state.extraPos[0] = state.pos;
      state.action = 'markdownlink-url';
    }
  },
  'markdownlink-url': (options, state) => {
    if (!state.char || state.char === '\n' || (
      state.extraPos[0] === state.pos - 1 && (
        (state.chars[state.pos] !== 'h' && state.chars[state.pos] !== 'H')
        || (state.chars[state.pos + 1] !== 't' && state.chars[state.pos + 1] !== 'T')
        || (state.chars[state.pos + 2] !== 't' && state.chars[state.pos + 2] !== 'T')
        || (state.chars[state.pos + 3] !== 'p' && state.chars[state.pos + 3] !== 'P')
        || ((
          (state.chars[state.pos + 4] !== 's' && state.chars[state.pos + 4] !== 'S')
          || (state.chars[state.pos + 5] !== ':' && state.chars[state.pos + 5] !== ':')
          || (state.chars[state.pos + 6] !== '/' && state.chars[state.pos + 6] !== '/')
          || (state.chars[state.pos + 7] !== '/' && state.chars[state.pos + 7] !== '/')
        ) && (
          (state.chars[state.pos + 4] !== ':' && state.chars[state.pos + 4] !== ':')
          || (state.chars[state.pos + 5] !== '/' && state.chars[state.pos + 5] !== '/')
          || (state.chars[state.pos + 6] !== '/' && state.chars[state.pos + 6] !== '/')
        ))
      )
    )) {
      // 匹配失败，回溯状态机
      state.pos = state.start;
      state.action = 'text';
    } else if (state.char === ')') {
      appendPendingText(state);
      state.contents.push({
        type: 'url',
        label: state.chars.slice(state.start + 1, state.extraPos[0] - 1).join(''),
        url: state.chars.slice(state.extraPos[0] + 1, state.pos).join(''),
      });
      state.pending = state.pos + 1;
      state.action = 'text';
    }
  },
};

/**
 * 解码正文
 * @param {string} text 原始内容
 * @param {SecretContentsParserOptions} options 功能开关与解码参数
 * @returns {SecretContentItem[]} 解码后的结果
 */
export const parseContents = (text: string, {
  emotions: emotionList = [],
  parsers: {
    topic: isParseTopic = true,
    emotion: isParseEmotion = true,
    serial: isParseSerial = true,
    post: isParsePost = true,
    name: isParseName = true,
    newline: isParseNewline = true,
    url: isParseURL = true,
    music: isParseMusic = true,
    markdownLink: isParseMarkdownLink = false,
  } = {},
}: SecretContentsParserOptions = {}): SecretContentItem[] => {
  // 初始化表情
  const emotions: Record<string, SecretEmotion> = {};
  let emotionMaxLen = 0;
  emotionList.forEach((e) => {
    emotions[e.text] = e;
    emotionMaxLen = Math.max(emotionMaxLen, e.text.length);
  });
  // 初始化状态机
  const fsm: ParseContentsFSM = {
    options: {
      parsers: {
        topic: isParseTopic,
        emotion: isParseEmotion,
        serial: isParseSerial,
        post: isParsePost,
        name: isParseName,
        newline: isParseNewline,
        url: isParseURL,
        music: isParseMusic,
        markdownlink: isParseMarkdownLink,
      },
      emotions,
      emotionMaxLen,
    },
    state: {
      chars: text.split(''),
      contents: [],
      action: 'text',
      pending: 0,
      start: 0,
      pos: 0,
      newlinePos: 0,
      extraPos: [],
      char: '',
      code: 0,
      type: 'unknown',
    },
  };
  // 逐字处理正文解析
  const state = fsm.state;
  const options = fsm.options;
  while (state.pos <= state.chars.length) {
    // 获取当前位置字符信息
    state.char = state.chars[state.pos];
    state.code = state.char ? state.char.charCodeAt(0) : -1;
    state.type = getCharType(state.code);
    fsm.next = ParseContentsActionHandle[state.action];
    // 状态机转移
    if (fsm.next) {
      fsm.next(options, state);
    } else {
      throw new Error(`Unhandled state: ${state.action}!`);
    }
    state.pos += 1;
  }
  return state.contents;
};

/**
 * 将后端返回的搜索结果数据转为前端组件数据格式
 * @param {SecretSearch[]} searchList 原始后端数据
 * @returns {SecretBlocksItem[]} 转码后的数据
 */
export const parseSecretBlocks = (searchList: SecretSearch[]): SecretBlocksItem[] => {
  const secretBlocks: SecretBlocksItem[] = [];
  searchList.forEach((p) => {
    if (p && p.type && p.data) {
      if (p.type === 'post') {
        const post = p.data as SecretPost;
        if (post.id) {
          secretBlocks.push({ type: 'post', data: post });
        }
        if (post.injectInfo) {
          secretBlocks.push({ type: 'inject', data: post.injectInfo });
        }
      } else if (p.type === 'user') {
        const user = p.data as UserFull;
        secretBlocks.push({ type: 'user', data: user });
      } else if (p.type === 'comment') {
        const comment = p.data as SecretComment;
        secretBlocks.push({ type: 'comment', data: comment });
      } else if (p.type === 'comment-post') {
        const commentPost = p.data as SecretCommentPost;
        secretBlocks.push({ type: 'comment-post', data: commentPost });
      }
    }
  });
  return secretBlocks;
};

/**
 * 将后端返回的纯帖子数据转为前端组件数据格式
 * @param {SecretPost[]} secretList 原始后端数据
 * @returns {SecretBlocksItem[]} 转码后的数据
 */
export const parseSecretListBlocks = (secretList: SecretPost[]): SecretBlocksItem[] => {
  const searchList: SecretSearch[] = secretList.map(s => ({ type: 'post', data: s }));
  return parseSecretBlocks(searchList);
};
